/** The signed-in-but-not-yet-a-member surface (issue #275).
 *
 * Google authenticates; only `lms_membership` authorizes. Between those two
 * facts sits a real person holding a valid session that `GET /me` answers with
 * a 403 — the designed pre-redemption state, not a failed sign-in. This page is
 * the name the product gives that state. Without it the person is redirected to
 * the sign-in form they just completed, told nothing, and offered a Google
 * button whose only effect is to arrive back here with a fresh token.
 */
import { useState } from "react";
import { Navigate } from "react-router";
import { Loader2 } from "lucide-react";

import { AuthCard } from "@/components/auth/AuthCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { homeForMember } from "@/navigation/member-routing";
import { useTenantPresentation } from "@/hooks/use-tenant-presentation";
import { SignInReturn } from "@/components/auth/SignInReturn";

/**
 * `none` is deliberately not worded as "your invitation is unredeemed".
 *
 * `lms_resolve_membership_role` filters on `is_active`, and an unknown or
 * inactive tenant is answered like a non-membership on purpose, so ONE 403
 * covers three different people: the invitee who has not redeemed, the member
 * whose access was withdrawn, and a tenant that is not serving. The frontend
 * cannot tell them apart, so the copy states only what is true of all three and
 * then offers each one its own next step.
 */
const FRAME = {
  none: {
    eyebrow: "Member access",
    heading: "Access not active",
    lede: "You are signed in, and this account has no active access to the portal.",
  },
  error: {
    eyebrow: "Member access",
    heading: "Access could not be confirmed",
    lede: "You are signed in, and we could not confirm your access just now.",
  },
  checking: {
    eyebrow: "Member access",
    heading: "Checking your access",
    lede: "Confirming what this account can reach in the portal.",
  },
} as const;

export function AccessPendingPage() {
  const { name, logoUrl } = useTenantPresentation();
  const { session, me, membershipStatus, isLoading, role, refreshMembership, signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  // Membership arriving while this page is open is the success case: the person
  // redeemed the invitation in another tab, or the retry below found it.
  if (membershipStatus === "active") {
    return isLoading
      ? <Navigate to="/" replace />
      : <SignInReturn fallback={homeForMember(role, me?.workspaces)} />;
  }
  // `anonymous` is the only SETTLED statement that there is no session, and it
  // covers the ordinary logged-out visit plus the moment after the sign-out
  // button below resolves. A bare `!session` test would also be true on the
  // first paint of a reload or a bookmark, because session restoration is
  // async — and bouncing then hands the person the sign-in form, and its
  // Google button, for the whole restore plus `/me` round trip. That is the
  // loop this page exists to end, so it must not be reintroduced here.
  if (membershipStatus === "anonymous") return <Navigate to="/sign-in" replace />;

  // Unresolved. `!session` is folded in for the same reason and for narrowing:
  // past this point a session is known to exist. The frame stays neutral —
  // announcing "access not active" while still checking states the accusation
  // as fact.
  if (membershipStatus === "loading" || !session) {
    return (
      <AuthCard brandName={name} logoUrl={logoUrl} {...FRAME.checking}>
        {/* The frame heading already says this; the body holds the shape of
            what follows (the account line, the explanation, the two acts) so
            nothing jumps. `role="status"` keeps the wait announced. */}
        <div role="status" aria-label="Checking your access" aria-busy="true" className="grid gap-4">
          <Skeleton className="h-16" />
          <Skeleton className="h-10 w-4/5" />
          <div className="grid gap-3 sm:grid-cols-2">
            <Skeleton className="h-[var(--control-height)]" />
            <Skeleton className="h-[var(--control-height)]" />
          </div>
        </div>
      </AuthCard>
    );
  }

  const email = session.user?.email ?? null;
  const frame = membershipStatus === "error" ? FRAME.error : FRAME.none;
  const busy = isLoading || signingOut;

  return (
    <AuthCard brandName={name} logoUrl={logoUrl} {...frame}>
      <div className="grid gap-4">
        {email && (
          <div className="rounded-control bg-well px-4 py-3">
            <div className="eyebrow">
              Signed in as
            </div>
            <div className="mt-1 break-words font-medium text-foreground">{email}</div>
          </div>
        )}

        {membershipStatus === "error" ? (
          <p className="text-ui text-muted-foreground">
            This is usually temporary. Try again in a moment. If it keeps happening, contact the
            academic office rather than signing in repeatedly.
          </p>
        ) : (
          <p className="text-ui text-muted-foreground">
            Your role here comes from an invitation, not from the Google account itself. If you were
            invited recently, open the invitation email sent to this address and follow its
            activation link, and check that you signed in with the account that was invited. If your
            access worked before, contact the academic office.
          </p>
        )}

        {/* Grid, not a flex row: `w-full` children in a row do not shrink, so
            the second button overflows a card that clips its own overflow and
            the way out of this screen disappears. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Button
            className="w-full"
            disabled={busy}
            onClick={() => {
              void refreshMembership();
            }}
          >
            {isLoading && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {membershipStatus === "error" ? "Try again" : "Check again"}
          </Button>
          <Button
            variant="outline"
            className="w-full"
            disabled={busy}
            onClick={() => {
              setSigningOut(true);
              void signOut().finally(() => setSigningOut(false));
            }}
          >
            {signingOut && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Sign out
          </Button>
        </div>
      </div>
    </AuthCard>
  );
}
